import React, { useState, useEffect, useRef } from 'react';

/**
 * PandlPasswordModal — Gates access to the P&L report.
 *
 * Logic:
 *   1. Read pandlPassword from sessionStorage (set at login for both normal & importer flows).
 *   2. If the typed password matches pandlPassword  → grant access (onSuccess).
 *   3. If pandlPassword is not set OR typed password doesn't match but equals '1234' → grant access.
 *   4. Otherwise → show "Invalid password" error.
 *
 * Props:
 *   isOpen    {boolean}   Controls modal visibility.
 *   onClose   {function}  Called when the modal is dismissed without success.
 *   onSuccess {function}  Called when the correct password is entered.
 */

const STATIC_FALLBACK = '1234';

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 4 8 4c2.02 0 3.82.668 5.167 1.957A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12 8 12c-2.02 0-3.82-.668-5.167-1.957A13.133 13.133 0 0 1 1.172 8z" />
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
    <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
    <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.708zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
  </svg>
);

const PandlPasswordModal = ({ isOpen, onClose, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Reset state every time modal opens
  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setError('');
      setShowPwd(false);
      // Focus the input on next tick (after DOM mount)
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  // Allow Enter key to submit
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleVerify();
  };

  const handleVerify = () => {
    const typed = password.trim();
    const stored = sessionStorage.getItem('pandlPassword') || '';

    // Grant access if: matches stored pandlPassword OR matches static fallback
    const isValid =
      (stored && typed === stored) ||
      typed === STATIC_FALLBACK;

    if (isValid) {
      setError('');
      onSuccess();
    } else {
      setError('Invalid password. Please try again.');
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    setShowPwd(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* ── Modal ── */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pandl-modal-title"
        style={{ zIndex: 1055 }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: 400 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">

            {/* Header */}
            <div
              className="modal-header border-0 pb-0 px-4 pt-4"
              style={{ background: 'var(--gtss-primary, #1a3bcc)' }}
            >
              <div className="d-flex align-items-center gap-2">
                {/* Lock icon */}
                <div
                  style={{
                    width: 36, height: 36,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="#fff" viewBox="0 0 16 16">
                    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                  </svg>
                </div>
                <div>
                  <h5
                    id="pandl-modal-title"
                    className="modal-title mb-0 fw-bold text-white"
                    style={{ fontSize: '1rem' }}
                  >
                    P&amp;L Report Access
                  </h5>
                  <p className="mb-0 text-white-50" style={{ fontSize: '11.5px' }}>
                    Enter your P&amp;L password to continue
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white ms-auto"
                onClick={handleClose}
                aria-label="Close"
              />
            </div>

            {/* Body */}
            <div className="modal-body px-4 py-4">
              <label
                htmlFor="pandl-password-input"
                className="form-label fw-semibold small text-uppercase text-dark mb-2"
                style={{ letterSpacing: '0.5px', fontSize: '11px' }}
              >
                Password
              </label>
              <div className="input-group">
                <input
                  ref={inputRef}
                  id="pandl-password-input"
                  type={showPwd ? 'text' : 'password'}
                  className={`form-control${error ? ' is-invalid' : ''}`}
                  placeholder="Enter P&L password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError('');
                  }}
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary d-flex align-items-center"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPwd ? <EyeSlashIcon /> : <EyeIcon />}
                </button>
                {error && (
                  <div className="invalid-feedback d-block mt-1 w-100" role="alert">
                    {error}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer border-0 px-4 pb-4 pt-0 gap-2">
              <button
                type="button"
                id="pandl-cancel-btn"
                className="btn btn-light fw-semibold px-4"
                style={{ borderRadius: 20 }}
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="button"
                id="pandl-verify-btn"
                className="btn fw-semibold text-white px-4 gtss-bg-primary border-0"
                style={{ borderRadius: 20 }}
                onClick={handleVerify}
                disabled={!password.trim()}
              >
                Verify &amp; Open
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default PandlPasswordModal;
